"use client";

import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import { formatDateIndonesia } from "@/lib/utils";
import { getDataSiswaLengkap } from "@/services/getData";
import type { DataPendaftaran, DataPilihanProdi, DataSiswa } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { md5 } from "js-md5";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Download,
  ExternalLink,
  GraduationCap,
  Info,
  Printer,
  User,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function PengumumanKelulusanPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const [showDetailNilai, setShowDetailNilai] = useState(false);

  const {
    timePassed,
    isPendingPeriode,
    isFetched: isFetchedPeriode,
  } = useTimePeriode();

  const {
    data: dataSiswa,
    isPending: isPendingDataSiswa,
    isFetched: isFetchedDataSiswa,
  } = useQuery<
    DataSiswa & {
      fakultas_nama?: string;
      fakultas_name?: string;
      jurusan_nama?: string;
      jurusan_name?: string;
      prodi_nama?: string;
      prodi_name?: string;
    }
  >({
    queryKey: ["dataSiswaPengumuman", user?.id, sessionPeriode?.id_sys_periode],
    queryFn: () => getDataSiswaLengkap(sessionPeriode?.id_sys_periode || ""),
    enabled: !!user && !!sessionPeriode?.id_sys_periode,
  });

  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;
  const dataPeriode = dataPendaftaran?.periode;
  const dataPilihanProdi: DataPilihanProdi[] | undefined =
    dataPendaftaran?.data_pilihan_prodi || undefined;
  const sortedDataPilihanProdi = dataPilihanProdi
    ? [...dataPilihanProdi].sort(
      (a: DataPilihanProdi, b: DataPilihanProdi) =>
        (a.urutan_program_studi ?? 0) - (b.urutan_program_studi ?? 0),
    )
    : [];

  const isLulus = !!dataPendaftaran?.is_lulus;

  const IS_JALUR_MANDIRI_LAINNYA_MASIH_BUKA =
    new Date() <= new Date("2026-06-30T23:00:00");

  useEffect(() => {
    if (
      isFetchedDataSiswa &&
      !dataPendaftaran &&
      isFetchedPeriode &&
      timePassed.waktu_pengumuman_kelulusan === false
    )
      navigate("/dashboard");
  }, [isFetchedPeriode, timePassed]);

  if (isPendingDataSiswa || isPendingPeriode) return <SkeletonTable />;

  const urlDetailNilai = `https://seleksi-pmb.undiksha.ac.id/detail_nilai/exsmbjm/${md5(
    dataPendaftaran?.nomor_pendaftaran || "",
  )}/${dataPendaftaran?.periode?.jalur_jenis?.kode_jalur}`;

  return (
    <div className="min-h-screen  dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 dark:bg-primary/20 rounded-2xl mb-4 shadow-sm">
            <GraduationCap className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">
            <span className="bg-gradient-to-r from-primary to-blue-600 dark:from-primary dark:to-blue-400 bg-clip-text text-transparent">
              Pengumuman Kelulusan {dataPendaftaran?.periode?.nama_jalur_jenis}
            </span>
          </h1>
          <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400 font-medium">
            <Calendar className="w-4 h-4" />
            <span>Tahun Akademik {dataPendaftaran?.periode?.tahun}</span>
          </div>
        </div>

        {/* Status Card */}
        <Card
          className={`mb-8 border-none shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden ${
            isLulus
              ? "bg-white dark:bg-slate-900 border-l-8 border-l-green-500"
              : "bg-white dark:bg-slate-900 border-l-8 border-l-red-500"
            }`}
        >
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div
                className={`p-4 rounded-full ${isLulus ? "bg-green-100 dark:bg-green-900/30" : "bg-red-100 dark:bg-red-900/30"}`}
              >
                {isLulus ? (
                  <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
                )}
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2
                  className={`text-2xl font-bold mb-2 ${isLulus ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
                >
                  {isLulus
                    ? `Selamat! ${dataSiswa?.nama_lengkap}`
                    : `Mohon Maaf, ${dataSiswa?.nama_lengkap}`}
                </h2>
                <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-lg">
                  {isLulus ? (
                    <>
                      Anda dinyatakan{" "}
                      <span className="font-bold underline decoration-2">
                        LULUS
                      </span>{" "}
                      seleksi Jalur {dataPeriode?.nama_jalur_jenis}.
                    </>
                  ) : (
                    <>
                      Anda dinyatakan{" "}
                      <span className="font-bold underline decoration-2">
                        TIDAK LULUS
                      </span>{" "}
                      seleksi Jalur {dataPeriode?.nama_jalur_jenis}.
                    </>
                  )}
                </p>

                {isLulus && (
                  <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 grid gap-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Program Studi:</span>
                      <span className="font-bold dark:text-slate-2d00">
                        {dataSiswa?.prodi_nama}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Fakultas:</span>
                      <span className="font-bold dark:text-slate-200">
                        {dataSiswa?.fakultas_nama}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm items-center pt-2 border-t dark:border-slate-700">
                      <span className="text-slate-500">Total Nilai:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-primary">
                          {dataPendaftaran?.total_nilai_smbjm || "-"}
                        </span>
                        {dataPendaftaran?.total_nilai_smbjm && (
                          <Badge
                            variant="outline"
                            className="cursor-pointer hover:bg-primary hover:text-white transition-colors"
                            onClick={() => setShowDetailNilai(true)}
                          >
                            Detail Nilai
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {isLulus && (
              <a href="/daftar-kembali" className="mt-8 flex flex-col gap-4">
                <Button
                  size="lg"
                  className="w-full text-lg h-14 font-bold shadow-lg shadow-primary/20"
                >
                  Daftar Kembali Sekarang
                </Button>
                <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                  Silakan lanjutkan proses pendaftaran kembali sesuai jadwal
                  yang telah ditentukan di bawah ini.
                </p>
              </a>
            )}

            {!isLulus ? (
              <div className="mt-6 flex flex-col items-center">
                <p className="text-slate-500 dark:text-slate-400 mb-4 text-center">
                  Terima kasih atas partisipasi Anda dalam proses seleksi ini.
                  <br />
                  {IS_JALUR_MANDIRI_LAINNYA_MASIH_BUKA && (
                    <span>
                      Masih ada kesempatan melalui Jalur Mandiri lainnya.
                    </span>
                  )}
                </p>
                <div className="flex gap-4">
                  {IS_JALUR_MANDIRI_LAINNYA_MASIH_BUKA && (
                    <Link to="/dashboard">
                      <Button className="flex gap-3">
                        Daftar Jalur Mandiri Lainnya
                      </Button>
                    </Link>
                  )}
                  {dataPendaftaran?.total_nilai_smbjm ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowDetailNilai(true)}
                      className="hover:bg-primary hover:text-white"
                    >
                      Lihat Rincian Skor Nilai
                    </Button>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            ) : (
              ""
            )}
          </CardContent>
        </Card>

        {/* Info Alerts (Jalur Lain / Peringatan) */}
        <div className="space-y-4 mb-8">
          {!isLulus && dataPendaftaran?.is_lulus_jalur_lain_periode_sama ? (
            <Alert
              variant="default"
              className="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-400"
            >
              <Info className="h-4 w-4" />
              <AlertTitle className="font-bold">Informasi Penting</AlertTitle>
              <AlertDescription>
                Sistem mendeteksi Anda sudah dinyatakan lulus pada jalur lain.{" "}
                {/* <strong>
                  {dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "prestasi"
                    ? "SMBJM Talentscouting"
                    : "SMBJM Nilai Raport"}
                </strong> */}
                .
              </AlertDescription>
            </Alert>
          ) : (
            ""
          )}

          {!isLulus &&
            (dataPendaftaran?.is_lulus_seleksi_nasional == 1 ||
              dataPendaftaran?.is_lulus_seleksi_ptn == 1) && (
              <Alert
                variant="warning"
                className="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              >
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="font-bold">
                  Informasi Kelulusan Jalur Lain
                </AlertTitle>
                <AlertDescription className="space-y-2 text-sm mt-2 leading-relaxed">
                  {dataPendaftaran?.is_lulus_seleksi_nasional == 1 && (
                    <li>
                      Anda dinyatakan LULUS DAN MENDAFTAR KEMBALI PADA JALUR
                      SNBT NASIONAL
                    </li>
                  )}
                  {dataPendaftaran?.is_lulus_seleksi_ptn == 1 && (
                    <li>
                      Anda telah dinyatakan lulus dan telah melakukan
                      pendaftaran kembali pada jalur SMBJM CBT Undiksha.
                    </li>
                  )}
                </AlertDescription>
              </Alert>
            )}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Biodata Section */}
          <div className="lg:col-span-2">
            <Card className="h-full bg-white dark:bg-slate-900 shadow-sm border-slate-100 dark:border-slate-800">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" /> Biodata Pendaftar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <Label className="text-slate-400 dark:text-slate-500 text-[10px] uppercase font-bold tracking-wider">
                      Nama Lengkap
                    </Label>
                    <p className="font-semibold dark:text-slate-200">
                      {dataSiswa?.nama_lengkap}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 dark:text-slate-500 text-[10px] uppercase font-bold tracking-wider">
                      Nomor Pendaftaran
                    </Label>
                    <p className="font-mono font-bold text-primary">
                      {dataPendaftaran?.nomor_pendaftaran}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 dark:text-slate-500 text-[10px] uppercase font-bold tracking-wider">
                      Asal Sekolah
                    </Label>
                    <p className="font-semibold dark:text-slate-200">
                      {dataSiswa?.sekolah?.nama_sekolah}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 dark:text-slate-500 text-[10px] uppercase font-bold tracking-wider">
                      Tempat, Tanggal Lahir
                    </Label>
                    <p className="font-semibold dark:text-slate-200">
                      {dataSiswa?.tempat_lahir},{" "}
                      {dataSiswa?.tanggal_lahir &&
                        formatDateIndonesia(dataSiswa?.tanggal_lahir, "medium")}
                    </p>
                  </div>
                </div>

                <Separator className="dark:bg-slate-800" />

                <div className="space-y-4">
                  <Label className="text-slate-400 dark:text-slate-500 text-[10px] uppercase font-bold tracking-wider block">
                    Pilihan Program Studi
                  </Label>
                  <div className="grid gap-3">
                    {sortedDataPilihanProdi?.map(
                      (pilihan: DataPilihanProdi) => (
                        <div
                          key={pilihan.id_data_pilihan_prodi}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            pilihan.is_lulus
                              ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900"
                              : "bg-slate-50 dark:bg-slate-800/40 border-slate-100 dark:border-slate-800"
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 flex items-center justify-center bg-white dark:bg-slate-800 rounded text-xs font-bold border dark:border-slate-700">
                              {pilihan.urutan_program_studi}
                            </span>
                            <span
                              className={`text-sm font-medium ${pilihan.is_lulus ? "text-green-700 dark:text-green-400" : "dark:text-slate-300"}`}
                            >
                              {pilihan.unitkerja?.nama}
                            </span>
                          </div>
                          {!!pilihan.is_lulus && (
                            <Badge className="bg-green-600 dark:bg-green-700">
                              Lulus
                            </Badge>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Section */}
          <div className="space-y-6">
            {/* Schedule Section */}
            {isLulus && (
              <Card className="bg-primary text-white shadow-lg border-none shadow-primary/20 overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Jadwal Daftar Kembali
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-2">
                  <div className="space-y-3">
                    <div className="bg-white/10 p-3 rounded-lg backdrop-blur-sm">
                      <p className="text-[10px] uppercase opacity-70 font-bold mb-1">
                        Pengisian UKT
                      </p>
                      <p className="text-xs font-semibold">
                        {formatDateIndonesia(
                          String(dataPeriode?.waktu_mulai_isi_ukt),
                        )}{" "}
                        -{" "}
                        {formatDateIndonesia(
                          String(dataPeriode?.waktu_selesai_isi_ukt),
                        )}
                      </p>
                    </div>
                    <div className="bg-white/10 p-3 rounded-lg backdrop-blur-sm">
                      <p className="text-[10px] uppercase opacity-70 font-bold mb-1">
                        Pembayaran UKT
                      </p>
                      <p className="text-xs font-semibold">
                        {formatDateIndonesia(
                          String(dataPeriode?.waktu_mulai_bayar_ukt),
                        )}{" "}
                        -{" "}
                        {formatDateIndonesia(
                          String(dataPeriode?.waktu_selesai_bayar_ukt),
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full text-xs"
                    asChild
                  >
                    {/* <Link
                      to={
                        dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "cbt"
                          ? "/panduan/Panduan-Pendaftaran-Kembali-Jalur Mandiri-CBT_Signed.pdf"
                          : "/panduan/Panduan-Pendaftaran-Kembali-Jalur Mandiri-CBT_Signed.pdf"
                      }
                      target="_blank"
                    >
                      <Download className="w-3 h-3 mr-2" /> Download Panduan
                    </Link> */ }
                    <a href={dataPeriode?.url_panduan_daftar_kembali ?? "#"} target="_blank" rel="noreferrer">
                      <Download className="w-3 h-3 mr-2" /> Download Panduan
                    </a>
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* CBT Certificate */}
            {dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "cbt" &&
              dataPendaftaran?.data_berkas_sertifikat?.url_tte_file && (
                <Card className="dark:bg-slate-900 border-slate-100 dark:border-slate-800">
                  <CardContent className="p-4 flex flex-col gap-3">
                    <p className="text-xs text-slate-500 font-medium">
                      Sertifikat Hasil CBT tersedia dalam format PDF:
                    </p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        window.open(
                          dataPendaftaran?.data_berkas_sertifikat?.url_tte_file,
                        )
                      }
                    >
                      <Printer className="w-4 h-4 mr-2" /> Cetak Sertifikat
                    </Button>
                  </CardContent>
                </Card>
              )}

            {/* Sanggah Box */}
            <Card className="border-amber-200 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-amber-800 dark:text-amber-400">
                  <AlertCircle className="w-4 h-4" /> Masa Sanggah Kelulusan
                </CardTitle>
              </CardHeader>
              <CardContent className="text-[11px] leading-relaxed text-amber-700 dark:text-amber-500 space-y-3">
                <p>
                  Masa sanggah dibuka pada{" "}
                  <strong>
                    {formatDateIndonesia(
                      String(
                        dataPendaftaran?.periode?.waktu_mulai_sanggah_kelulusan,
                      ),
                    )}
                  </strong>{" "}
                  s/d{" "}
                  <strong>
                    {formatDateIndonesia(
                      String(
                        dataPendaftaran?.periode
                          ?.waktu_selesai_sanggah_kelulusan,
                      ),
                    )}
                  </strong>
                  .
                </p>
                <Separator className="bg-amber-200 dark:bg-amber-900" />
                <div className="grid gap-2">
                  <a
                    href="https://ppid.undiksha.ac.id/pengaduan"
                    target="_blank"
                    className="hover:text-primary flex items-center gap-1"
                  >
                    <ExternalLink size={10} /> ppid.undiksha.ac.id
                  </a>
                  <a
                    href="https://wbs.kemdiktisaintek.go.id"
                    target="_blank"
                    className="hover:text-primary flex items-center gap-1"
                  >
                    <ExternalLink size={10} />
                    wbs.kemdiktisaintek.go.id
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal Detail Nilai */}
      <Modal
        show={showDetailNilai}
        onClose={() => setShowDetailNilai(false)}
        className="h-[80vh] md:w-[60vw] w-950vw]"
      >
        <div className="h-[80vh] md:w-[60vw] w-[95vw]">
          <iframe
            src={urlDetailNilai}
            width="100%"
            height="100%"
            className="absolute h-full w-full"
            allowFullScreen
          />
          <div className="absolute -bottom-16 left-3 right-3">
            <Button
              variant={"outline"}
              className="w-full"
              onClick={() => setShowDetailNilai(false)}
            >
              Tutup
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
